import { Icon, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import classNames from "classnames";
import { DataDisplayRecord } from "components/data-display";
import { DateToLocal } from "components/dates";
import { GridLayout } from "components/grid";
import { ShowHide } from "components/layout";
import PropTypes from "prop-types";
import React, { Fragment, useMemo } from "react";

function getIconAndTextComponent(iconIntent, iconName, text) {
    return (
        <div className="inline-items">
            <Icon intent={iconIntent} icon={iconName} size={12} />
            <span>{text}</span>
        </div>
    );
}

export function GreyFleetInsuranceAIResults({ loading, insuranceDetails }) {
    const validIconName = IconNames.TickCircle;
    const invalidIconName = IconNames.Error;
    const notProvidedText = "Not provided";
    
    const aiResultAvailable = useMemo(() => {
        return !loading && !insuranceDetails?.ai.errored && insuranceDetails?.ai.completed === true;
    }, [loading, insuranceDetails]);

    const nameOnPolicyData = useMemo(() => {
        if (insuranceDetails?.ai?.nameMatches) {
            return getIconAndTextComponent(Intent.SUCCESS, validIconName, insuranceDetails?.employeeName);
        }
        return getIconAndTextComponent(Intent.WARNING, invalidIconName, insuranceDetails?.employeeName);
    }, [insuranceDetails]);

    const vehicleOnPolicyData = useMemo(() => {
        if (insuranceDetails?.ai?.registrationMatches) {
            return getIconAndTextComponent(Intent.SUCCESS, validIconName, insuranceDetails?.registration);
        }
        return getIconAndTextComponent(Intent.WARNING, invalidIconName, insuranceDetails?.registration);
    }, [insuranceDetails]);

    const startDateOnPolicyData = useMemo(() => {
        const intent = insuranceDetails?.ai?.datesInRange ? Intent.SUCCESS : Intent.WARNING;
        const iconName = insuranceDetails?.ai?.datesInRange ? validIconName : invalidIconName;
        const date = insuranceDetails?.userInput?.startDate ? <DateToLocal key={`startdate-${insuranceDetails?.checkId}`} format='DD/MM/YYYY'>{insuranceDetails?.userInput?.startDate}</DateToLocal> : notProvidedText;
        return getIconAndTextComponent(intent, iconName, date);
    }, [insuranceDetails, notProvidedText]);

    const expiryDateOnPolicyData = useMemo(() => {
        const intent = insuranceDetails?.ai?.datesInRange ? Intent.SUCCESS : Intent.WARNING;
        const iconName = insuranceDetails?.ai?.datesInRange ? validIconName : invalidIconName;
        const date = insuranceDetails?.userInput?.endDate ? <DateToLocal key={`expirydate-${insuranceDetails?.checkId}`} format='DD/MM/YYYY'>{insuranceDetails?.userInput?.endDate}</DateToLocal> : notProvidedText;
        return getIconAndTextComponent(intent, iconName, date);
    }, [insuranceDetails, notProvidedText]);

    const businessUseData = useMemo(() => {
        if (insuranceDetails?.ai?.businessUse) {
            return getIconAndTextComponent(Intent.SUCCESS, validIconName, "Valid");
        }
        return getIconAndTextComponent(Intent.WARNING, invalidIconName, "Invalid");
    }, [insuranceDetails]);

    return (
        <ShowHide
            evaluator={loading || aiResultAvailable}
            show={
                <Fragment>
                    <div className="spacer-bottom">
                        <h2>AI Results</h2>
                        <GridLayout gap={40} numberOfColumns={4} className={classNames({ "bp6-skeleton": loading })}>
                            <DataDisplayRecord header="Driver Named on Policy" data={nameOnPolicyData} />
                            <DataDisplayRecord header="Vehicle Named on Policy" data={vehicleOnPolicyData} />
                            <DataDisplayRecord header="Policy Start Date" data={startDateOnPolicyData} />
                            <DataDisplayRecord header="Policy Expiry Date" data={expiryDateOnPolicyData} />
                            <DataDisplayRecord header="Required cover" data={businessUseData} />
                        </GridLayout>
                    </div>
                    <DataDisplayRecord header="Relevant Extract From Document" data={
                            insuranceDetails?.ai?.businessUseText && insuranceDetails.ai.businessUseText.trim() !== ""
                                ? insuranceDetails.ai.businessUseText
                                : "No relevant extract available"
                        }
                    />
                </Fragment>
            }
        />
    );
}

GreyFleetInsuranceAIResults.propTypes = {
    loading: PropTypes.bool.isRequired,
    insuranceDetails: PropTypes.object
};
